import { useState, useEffect } from 'react';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import Head from 'next/head';
import PageLayout from '@/components/PageLayout';
import useWindowUnderThreshold from '@/hooks/useWindowUnderThreshold';

import { transformGiftFromDetails, transformGift, getGiftReceiptDetails } from '@/utils/gifts';
import { getLayoutProps } from '@/utils/layout/layout';
import User from '@/store/user';
import { GiftsSideSheetStore, SideSheetStore } from '@/store/sideSheet';

import { GiftCustomization, GiftDetails, ProductDetailsImage } from '@desquared/wave-components-library';
import { getGiftDetailsV3, getGiftInfoV3, submitGiftOrder } from '@/api/Gifts';

import GiftsAside from '@/components/GiftsAside/GiftsAside';
import { getCMSHomepageGrid } from '@/api/cms';
import { getOrFetchConfiguration } from '@/utils/getConfiguration';
import useGiftDeliveryOptions from '@/hooks/useGiftDeliveryOptions';
import GiftReceipt from '@/components/GiftReceipt/GiftReceipt';
import styles from '@/styles/Gifts.module.css';
import { useTranslations } from 'next-intl';
import { useDateFormatting } from '@/contexts/DateFormattingProvider';
import { useRouter } from 'next/router';

export default function Gifts({ responsiveLogo, giftInfo, giftDetails, giftsConfig }) {
  const isMobile = useWindowUnderThreshold(850);
  const [sideSheet, setSideSheet] = useState(false);
  const [user, setUser] = useState();
  const [gift, setGift] = useState(null);
  const [comment, setComment] = useState('');
  const [config, setConfig] = useState();
  const [showReceipt, setShowReceipt] = useState(null);
  const [receiptDetails, setReceiptDetails] = useState();
  const router = useRouter();

  const [selectedDeliveryIndex, setSelectedDeliveryIndex] = useState(0);
  const [showStores, setShowStores] = useState(false);
  const [storeId, setStoreId] = useState(undefined);
  const { timeFormat } = useDateFormatting();

  const t = useTranslations();

  useEffect(() => {
    const user$ = User.subscribe(_user => {
      if (isEmpty(_user)) {
        router.replace('/');
        return;
      }

      async function getConfiguration() {
        const configuration = await getOrFetchConfiguration();
        setConfig(configuration);
        return;
      }

      const GiftsSideSheetStore$ = GiftsSideSheetStore.subscribe(setSideSheet);
      getConfiguration();
      setUser(_user);

      return () => {
        GiftsSideSheetStore$.unsubscribe();
      };
    });
    return () => user$.unsubscribe();
  }, []);

  useEffect(() => {
    const giftFormatted = {
      ...transformGift(giftInfo, user?.totalLoyaltyPoints, t),
      ...transformGiftFromDetails(giftDetails),
    };

    setGift(giftFormatted);
  }, [user, giftInfo, giftDetails]);

  useEffect(() => {
    if (selectedDeliveryIndex === 1) setShowStores(true);
    else setShowStores(false);
  }, [selectedDeliveryIndex]);

  const { deliveryOptions } = useGiftDeliveryOptions(config, selectedDeliveryIndex);

  if (!gift) return null;

  const images = [gift.image];

  const onConfirmRedemption = () => {
    const deliveryOption = deliveryOptions.find(option => option.index === selectedDeliveryIndex);
    submitGiftOrder(gift.id, deliveryOption?.type || 'TAKEAWAY', comment, storeId).then(result => {
      const _receiptDetails = getGiftReceiptDetails(result, t, timeFormat);
      setReceiptDetails(_receiptDetails);
      setShowReceipt(true);
    });
  };

  return (
    <>
      <Head>
        <title>{process.env.displayName}</title>
        <meta name="description" content={`App for ${process.env.displayName} by Wave Grocery`} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="icon" href={responsiveLogo} />
      </Head>

      <main className={styles.mainContainer}>
        <div className={styles.productsWrapper}>
          <div className={styles.productColumns}>
            <div className={styles.productColumn}>
              <ProductDetailsImage
                selectedImage={images?.[0]}
                hasArrows={false}
                selectedImageIndex={0}
                selectedList={[]}
                hasOffer={false}
                hasIcon={false}
                classOption={styles.image}
              />

              {isMobile ? (
                <div className={styles.productDetails}>
                  <GiftCustomization
                    title={gift.displayName}
                    giftPoints={gift.giftPoints}
                    userPoints={gift.userPoints}
                    pointsText={t('gifts.details.points')}
                    showProgressBar={gift.showProgressBar}
                    progress={gift.progress}
                    progressInformativeText={gift.informativeText}
                    available={gift.available}
                    buttonText={t('gifts.details.redeem')}
                    onButtonClick={GiftsSideSheetStore.open}
                    classOption={styles.customization}
                  />
                </div>
              ) : null}
              <div className={cn(styles.productDetails, styles.productDescription)}>
                <GiftDetails
                  detailsTitle={t('gifts.details.title')}
                  detailsText={gift?.description}
                  list={gift?.characteristics}
                  classOption={styles.characteristics}
                />
              </div>
            </div>

            {isMobile ? null : (
              <div className={styles.productInfo}>
                <GiftCustomization
                  title={gift.displayName}
                  giftPoints={gift.giftPoints}
                  userPoints={gift.userPoints}
                  pointsText={t('gifts.details.points')}
                  showProgressBar={gift.showProgressBar}
                  progress={gift.progress}
                  progressInformativeText={gift.informativeText}
                  available={gift.available}
                  buttonText={t('gifts.details.redeem')}
                  onButtonClick={GiftsSideSheetStore.open}
                  classOption={styles.customization}
                />
              </div>
            )}
          </div>
        </div>
        {sideSheet && (
          <GiftsAside
            firstName={user?.firstName}
            lastName={user?.lastName}
            onConfirmRedemption={onConfirmRedemption}
            alertText={giftsConfig?.alert_text}
            onSelectDeliveryOption={index => setSelectedDeliveryIndex(index)}
            onSelectStore={setStoreId}
            listAddress={[]}
            onSelectAllStores={() => {
              GiftsSideSheetStore.close();
              // SideSheetStore.open();
            }}
            deliveryOptions={deliveryOptions}
            onChangeComment={setComment}
            comment={comment}
            onClose={() => setComment('')}
            showStores={showStores}
          />
        )}
        <GiftReceipt
          showModal={showReceipt}
          setShowModal={setShowReceipt}
          greeting={t('gifts.receipt.greeting')}
          message={t('gifts.receipt.message')}
          receiptDetailsList={receiptDetails}
        />
      </main>
    </>
  );
}

Gifts.getLayout = function getLayout(page) {
  return <PageLayout>{page}</PageLayout>;
};

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps(context) {
  //ToDo: this should change once we have real slugs
  const giftId = context.params.slug[0];
  const layoutProps = await getLayoutProps({ headerProps: { selectedIndex: 0 } }, context.locale);
  let giftDetails, giftInfo;
  try {
    giftInfo = await getGiftInfoV3(giftId, context.locale);
  } catch (err) {
    giftInfo = null;
  }
  try {
    giftDetails = await getGiftDetailsV3(giftId, context.locale);
  } catch (err) {
    giftDetails = null;
  }
  let giftsCmsResults;
  try {
    giftsCmsResults = await getCMSHomepageGrid('gifts_page', context.locale);
  } catch (err) {
    giftsCmsResults = null;
    console.log(JSON.stringify(err));
  }

  const messages = {
    common: (await import(`../../../../prebuild-data/i18n/${context.locale}/common.json`)).default,
    gifts: (await import(`../../../../prebuild-data/i18n/${context.locale}/gifts.json`)).default,
  };

  return {
    props: {
      ...layoutProps,
      giftInfo: giftInfo || null,
      giftDetails: giftDetails || null,
      giftsConfig: giftsCmsResults?.data?.data?.[0]?.content || null,
      messages,
    },
    revalidate: 1800,
  };
}
